import React, { useEffect, memo } from 'react'
import { ValueFormatterParams } from 'ag-grid-community'
import { AGDataGrid } from 'components'
import { ExtendedColumn } from 'components/DataGrid/ExtendedColumn'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { getTop5AccountsMockData } from 'utils/clientMockData'
import './MidTop5DataAGGrid.scss'

interface MidTop5DataGridProps {
    testId: string
    gridRef: any
    dataSource: any
    isLoading: boolean
    paginationTotal: number
    showClientTypeIssuerUI: boolean
    dataGridColumns: ExtendedColumn[]
    setDataGridColumns: (columns: ExtendedColumn[]) => void
    chargebacksSum?: number
}

export const MidTop5DataAGGrid: React.FC<MidTop5DataGridProps> = ({
    testId,
    gridRef,
    dataSource,
    isLoading,
    paginationTotal,
    showClientTypeIssuerUI,
    dataGridColumns,
    setDataGridColumns,
    chargebacksSum,
}) => {
    const { themeVariantObj } = useThemeStyling()
    const { client } = useFeatureToggle('CLIENT')

    const valueFormatter = (params: ValueFormatterParams) => {
        return Number(params.value).toLocaleString('en-US')
    }

    const splitIntoFiveDifferentDetailed = (chargebacksSum: number) => {
        const base = Math.floor(chargebacksSum / 5)

        // small offsets to make totals different
        const offsets = [10, -1, -3, 3, 0]

        // make sure totals are never negative
        const totals = offsets.map((o) => Math.max(base + o, 0))

        // adjust last value to match exact sum
        const currentSum = totals.reduce((a, b) => a + b, 0)
        totals[totals.length - 1] += chargebacksSum - currentSum

        // ensure last total is also not negative
        totals[totals.length - 1] = Math.max(totals[totals.length - 1], 0)

        // target total fraud = 36% of chargebacksSum
        let targetFraudSum = Math.floor(chargebacksSum * 0.36)

        // if target fraud is bigger than sum of totals (can happen if some totals are 0)
        targetFraudSum = Math.min(
            targetFraudSum,
            totals.reduce((a, b) => a + b, 0)
        )

        // randomly split target fraud among totals
        const result: { total: number; fraud: number; nonFraud: number }[] = []
        let remainingFraud = targetFraudSum

        totals.forEach((total, idx) => {
            const maxFraud = Math.min(total, remainingFraud)
            const fraud =
                idx === totals.length - 1
                    ? remainingFraud
                    : Math.floor(Math.random() * (maxFraud + 1))
            const nonFraud = Math.max(total - fraud, 0)
            result.push({ total, fraud, nonFraud })
            remainingFraud -= fraud
        })

        return result
    }

    const top5Values = splitIntoFiveDifferentDetailed(chargebacksSum ?? 0)

    const mockData = getTop5AccountsMockData(themeVariantObj)

    const merchantColumns: ExtendedColumn[] = [8].includes(themeVariantObj.id)
        ? [
              {
                  Header: 'Brand',
                  accessor: 'brand',
                  type: 'string',
                  minWidth: 90,
              },
              {
                  Header: 'New',
                  accessor: 'new',
                  type: 'number',
                  format: valueFormatter,
                  minWidth: 75,
                  cellStyle: { textAlign: 'center' },
                  headerClass: 'ag-header-cell-center',
              },
              {
                  Header: 'Non-fraud',
                  accessor: 'nonFraud',
                  type: 'number',
                  format: valueFormatter,
                  minWidth: 110,
                  cellStyle: { textAlign: 'center' },
                  headerClass: 'ag-header-cell-center',
              },
              {
                  Header: '% Non-fraud',
                  accessor: 'percentNonFraud',
                  type: 'number',
                  minWidth: 125,
                  cellStyle: { textAlign: 'right' },
              },
              {
                  Header: 'Fraud',
                  accessor: 'fraud',
                  type: 'number',
                  format: valueFormatter,
                  minWidth: 75,
                  cellStyle: { textAlign: 'center' },
                  headerClass: 'ag-header-cell-center',
              },
              {
                  Header: '% Fraud',
                  accessor: 'percentFraud',
                  type: 'number',
                  minWidth: 110,
                  cellStyle: { textAlign: 'right' },
              },
              {
                  Header: 'In Review',
                  accessor: 'defended',
                  type: 'number',
                  format: valueFormatter,
                  minWidth: 110,
                  cellStyle: { textAlign: 'center' },
                  headerClass: 'ag-header-cell-center',
              },
              {
                  Header: 'Completed',
                  accessor: 'completed',
                  type: 'number',
                  format: valueFormatter,
                  minWidth: 120,
                  cellStyle: { textAlign: 'center' },
                  headerClass: 'ag-header-cell-center',
              },
              {
                  Header: 'Not Represented',
                  accessor: 'notRepresented',
                  type: 'number',
                  format: valueFormatter,
                  minWidth: 175,
                  cellStyle: { textAlign: 'center' },
                  headerClass: 'ag-header-cell-center',
              },
          ]
        : [
              {
                  Header: 'MID',
                  accessor: client === 'bluesnap' ? 'midAlias' : 'mid',
                  type: 'string',
                  minWidth: 110,
              },
              {
                  Header: 'Merchant Descriptor',
                  accessor: 'midDescriptor',
                  type: 'string',
                  minWidth: 190,
              },
              {
                  Header: 'MID Alias',
                  accessor: 'midAlias',
                  type: 'string',
                  minWidth: 110,
              },
              {
                  Header: 'Won',
                  accessor: 'won',
                  type: 'number',
                  Cell: memo((params: any) => {
                      const getValue = (idx: number) => {
                          switch (idx) {
                              case 0:
                                  return 56
                              case 1:
                                  return 28
                              case 2:
                                  return 11
                              case 3:
                                  return 20
                              case 4:
                                  return 55
                              default:
                                  return null
                          }
                      }
                      return <div>{getValue(params.rowIndex)}</div>
                  }),
                  minWidth: 120,
                  cellStyle: {
                      textAlign: 'center',
                      backgroundColor: '#dcfce7',
                      color: '#166534',
                      borderColor: '#dcfce7',
                  },
                  headerClass: 'ag-header-cell-center',
              },
              {
                  Header: 'Lost',
                  accessor: 'lost',
                  type: 'number',
                  Cell: memo((params: any) => {
                      const getValue = (idx: number) => {
                          switch (idx) {
                              case 0:
                                  return 24
                              case 1:
                                  return 12
                              case 2:
                                  return 4
                              case 3:
                                  return 9
                              case 4:
                                  return 23
                              default:
                                  return null
                          }
                      }
                      return <div>{getValue(params.rowIndex)}</div>
                  }),
                  minWidth: 120,
                  cellStyle: {
                      textAlign: 'center',
                      backgroundColor: '#fee2e2',
                      color: '#991b1b',
                      borderColor: '#fee2e2',
                  },
                  headerClass: 'ag-header-cell-center',
              },
              {
                  Header: 'New',
                  accessor: 'newMock',
                  type: 'number',
                  format: (params: ValueFormatterParams) => {
                      return top5Values?.[params.data.rowIndex]?.total
                  },
                  cellStyle: { textAlign: 'center' },
                  headerClass: 'ag-header-cell-center',
              },
              {
                  Header: 'Non-fraud',
                  accessor: 'nonFraudMock',
                  type: 'number',
                  format: (params: ValueFormatterParams) => {
                      return top5Values?.[params.data.rowIndex]?.nonFraud
                  },
                  minWidth: 110,
                  cellStyle: { textAlign: 'center' },
                  headerClass: 'ag-header-cell-center',
              },
              {
                  Header: '% Non-fraud',
                  accessor: 'percentNonFraudMock',
                  type: 'number',
                  minWidth: 134,
                  cellStyle: { textAlign: 'center' },
                  format: (params: ValueFormatterParams) => {
                      const nonFraudpercent = top5Values?.[params.data.rowIndex]
                          ? (
                                (top5Values?.[params.data.rowIndex]?.nonFraud /
                                    top5Values?.[params.data.rowIndex]?.total) *
                                100
                            ).toFixed(2)
                          : 0
                      return `${nonFraudpercent}%`
                  },
              },
              {
                  Header: 'Fraud',
                  accessor: 'fraudMock',
                  type: 'number',
                  format: (params: ValueFormatterParams) => {
                      return top5Values?.[params.data.rowIndex]?.fraud
                  },
                  minWidth: 90,
                  cellStyle: { textAlign: 'center' },
                  headerClass: 'ag-header-cell-center',
              },
              {
                  Header: '% Fraud',
                  accessor: 'percentFraudMock',
                  type: 'number',
                  minWidth: 110,
                  cellStyle: { textAlign: 'center' },
                  format: (params: ValueFormatterParams) => {
                      const nonFraudpercent = top5Values?.[params.data.rowIndex]
                          ? (
                                (top5Values?.[params.data.rowIndex]?.fraud /
                                    top5Values?.[params.data.rowIndex]?.total) *
                                100
                            ).toFixed(2)
                          : 0
                      return `${nonFraudpercent}%`
                  },
              },
              {
                  Header: 'In Review',
                  accessor: 'defended',
                  type: 'number',
                  format: valueFormatter,
                  minWidth: 110,
                  cellStyle: { textAlign: 'center' },
                  headerClass: 'ag-header-cell-center',
              },
          ]

    const issuerColumns: ExtendedColumn[] = [
        {
            Header: 'Account Number',
            accessor: 'mid',
            type: 'string',
            minWidth: 170,
            cellStyle: { textAlign: 'center' },
            headerClass: 'ag-header-cell-center',
        },
        {
            Header: 'Client Name',
            accessor: 'businessName',
            type: 'string',
            minWidth: 166,
            cellStyle: { textAlign: 'center' },
            headerClass: 'ag-header-cell-center',
        },
        {
            Header: 'Account Alias',
            accessor: 'midAlias',
            type: 'string',
            minWidth: 150,
            cellStyle: { textAlign: 'center' },
            headerClass: 'ag-header-cell-center',
        },
        {
            Header: 'Total Cases',
            accessor: 'totalCases',
            type: 'number',
            format: valueFormatter,
            minWidth: 120,
            cellStyle: { textAlign: 'center' },
            headerClass: 'ag-header-cell-center',
        },
        {
            Header: 'Won',
            accessor: 'won',
            type: 'number',
            format: valueFormatter,
            minWidth: 95,
            cellStyle: {
                textAlign: 'center',
                backgroundColor: '#dcfce7',
                color: '#166534',
                borderColor: '#dcfce7',
            },
            headerClass: 'ag-header-cell-center',
        },
        {
            Header: 'Lost',
            accessor: 'lost',
            type: 'number',
            format: valueFormatter,
            minWidth: 95,
            cellStyle: {
                textAlign: 'center',
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                borderColor: '#fee2e2',
            },
            headerClass: 'ag-header-cell-center',
        },
        {
            Header: 'Non-Fraud',
            accessor: 'nonFraud',
            type: 'number',
            format: valueFormatter,
            minWidth: 125,
            cellStyle: { textAlign: 'center' },
            headerClass: 'ag-header-cell-center',
        },
        {
            Header: '% Non-Fraud',
            accessor: 'percentNonFraud',
            type: 'number',
            minWidth: 150,
            cellStyle: { textAlign: 'center' },
            headerClass: 'ag-header-cell-center',
        },
        {
            Header: 'Fraud',
            accessor: 'fraud',
            type: 'number',
            format: valueFormatter,
            minWidth: 95,
            cellStyle: { textAlign: 'center' },
            headerClass: 'ag-header-cell-center',
        },
        {
            Header: '% Fraud',
            accessor: 'percentFraud',
            type: 'number',
            minWidth: 115,
            cellStyle: { textAlign: 'center' },
            headerClass: 'ag-header-cell-center',
        },
        {
            Header: 'Closed',
            accessor: 'closed',
            type: 'number',
            format: valueFormatter,
            minWidth: 95,
            cellStyle: { textAlign: 'center' },
            headerClass: 'ag-header-cell-center',
        },
        {
            Header: '% Closed',
            accessor: 'percentClosed',
            type: 'number',
            minWidth: 115,
            cellStyle: { textAlign: 'center' },
            headerClass: 'ag-header-cell-center',
        },
    ]

    const columns = [8].includes(themeVariantObj.id)
        ? merchantColumns
        : showClientTypeIssuerUI
        ? issuerColumns
        : merchantColumns

    useEffect(() => {
        if (columns?.length && chargebacksSum) {
            setDataGridColumns(columns)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chargebacksSum])

    const defaultColDef = {
        flex: 1,
        minWidth: 80,
        resizable: true,
        menuTabs: [],
    }

    // Create a mock datasource for static data
    const createMockDataSource = (data: any[]) => {
        return {
            getRows: (params: any) => {
                console.log('Mock datasource getRows called with:', params)
                setTimeout(() => {
                    params.success({
                        rowData: data,
                        rowCount: data.length,
                    })
                }, 100)
            },
        }
    }

    const finalDataSource = React.useMemo(
        () =>
            showClientTypeIssuerUI || [8].includes(themeVariantObj.id)
                ? createMockDataSource(mockData.data)
                : dataSource,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dataSource, showClientTypeIssuerUI]
    )

    const finalTotalEntries =
        showClientTypeIssuerUI || [8].includes(themeVariantObj.id)
            ? mockData.data.length
            : paginationTotal

    return (
        <div id={'dashboardMidTop5DataAGGrid'}>
            <AGDataGrid
                gridRef={gridRef}
                gridName={'top5Mid'}
                fileName={'top-5-mid'}
                columns={dataGridColumns}
                dataSource={finalDataSource}
                loading={isLoading}
                testId={testId}
                totalEntries={finalTotalEntries}
                defaultColDef={defaultColDef}
                rowSelectionType={'single'}
                disableRowClickSelection
                paginate={false}
                showClientTypeIssuerUI={showClientTypeIssuerUI}
                gridThemeType={'materialSlim'}
                enableSideBarPanel={false}
            />
        </div>
    )
}
